import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Favorite as FavoriteIcon,
  Schedule as ScheduleIcon,
  Assignment as ApplicationIcon,
  LocationOn as LocationIcon,
  Pets as PetsIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as VaccinatedIcon,
  LocalHospital as SterilizedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import {
  getAdoptableAnimalsCatalog,
  getAdoptableAnimalDetail,
  submitAdoptionApplication,
} from '@services/publicSheltersService';
import { API_ORIGIN } from '@config/api';

const PAGE_SIZE = 12;

interface CatalogAnimal {
  id: string;
  name: string;
  species: string;
  breed?: string | null;
  age_months?: number | null;
  gender?: string | null;
  size?: string | null;
  color?: string | null;
  description?: string | null;
  photos: string[];
  shelter: {
    id: string;
    name: string;
    city: string;
    county: string;
  };
  sterilized?: boolean;
  vaccinated?: boolean;
  dewormed?: boolean;
  microchipped?: boolean;
  status: string;
  created_at?: string;
}

function resolvePhotoUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${API_ORIGIN}${url}`;
  return url;
}

function mapRowToCatalogAnimal(row: any): CatalogAnimal {
  const photos: string[] = Array.isArray(row.photos) ? row.photos : [];
  return {
    id: row.id,
    name: row.name,
    species: row.species,
    breed: row.breed,
    age_months: row.age_months,
    gender: row.gender,
    size: row.size,
    color: row.color,
    description: row.description,
    photos,
    shelter: row.shelter || {
      id: row.shelter_id,
      name: '',
      city: '',
      county: '',
    },
    sterilized: !!row.sterilized,
    vaccinated: !!row.vaccinated,
    dewormed: !!row.dewormed,
    microchipped: !!row.microchipped,
    status: row.status || 'available',
    created_at: row.created_at,
  };
}

const AnimalsListingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [animals, setAnimals] = useState<CatalogAnimal[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({
    species: '',
    ageRange: '',
    size: '',
    gender: '',
    status: 'available',
    city: '',
    county: '',
    q: '',
  });
  const [selectedAnimal, setSelectedAnimal] = useState<CatalogAnimal | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applicationNotes, setApplicationNotes] = useState('');
  const [applicationSubmitting, setApplicationSubmitting] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  const loadAnimals = useCallback(async () => {
    try {
      setLoading(true);
      setListError('');
      const params: Record<string, string | number> = {
        page,
        limit: PAGE_SIZE,
        sort: 'recent',
      };
      if (filters.species) params.species = filters.species;
      if (filters.ageRange) params.age = filters.ageRange;
      if (filters.size) params.size = filters.size;
      if (filters.gender) params.gender = filters.gender;
      if (filters.status) params.status = filters.status;
      if (filters.city.trim()) params.city = filters.city.trim();
      if (filters.county.trim()) params.county = filters.county.trim();
      if (filters.q.trim()) params.q = filters.q.trim();

      const res = await getAdoptableAnimalsCatalog(params);
      if (!res.success) {
        setListError(res.error || 'Nu am putut încărca catalogul.');
        setAnimals([]);
        setTotal(0);
        setPages(1);
        return;
      }
      const rawItems = res.data?.items ?? [];
      setAnimals(rawItems.map(mapRowToCatalogAnimal));
      setTotal(res.data?.total ?? 0);
      setPages(res.data?.pages ?? 1);
    } catch (error: any) {
      console.error('Error loading adoptable animals:', error);
      setListError(error?.response?.data?.error || 'Eroare la încărcarea catalogului.');
      setAnimals([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadAnimals();
  }, [loadAnimals]);

  const handleViewAnimal = async (animal: CatalogAnimal) => {
    setSelectedAnimal(animal);
    setDetailDialogOpen(true);
    setDetailLoading(true);
    try {
      const res = await getAdoptableAnimalDetail(animal.id);
      if (res.success && res.data) {
        setSelectedAnimal(mapRowToCatalogAnimal(res.data));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApplyForAdoption = (animal: CatalogAnimal) => {
    setSelectedAnimal(animal);
    setApplicationNotes('');
    setApplicationMessage(null);
    setApplicationDialogOpen(true);
  };

  const handleScheduleVisit = (animal: CatalogAnimal) => {
    navigate(`/client/shelters/${animal.shelter.id}/contact`);
  };

  const handleSubmitApplication = async () => {
    if (!selectedAnimal) return;
    if (!user) {
      navigate('/login', { state: { from: '/adoptie' } });
      return;
    }
    setApplicationSubmitting(true);
    setApplicationMessage(null);
    try {
      const res = await submitAdoptionApplication(selectedAnimal.id, {
        notes: applicationNotes.trim() || undefined,
      });
      if (!res.success) {
        setApplicationMessage({ type: 'error', text: res.error || 'Cererea nu a putut fi trimisă.' });
        return;
      }
      setApplicationMessage({ type: 'success', text: 'Cererea a fost înregistrată. Adăpostul te va contacta.' });
      setTimeout(() => {
        setApplicationDialogOpen(false);
        setApplicationMessage(null);
      }, 2000);
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Eroare la trimiterea cererii.';
      setApplicationMessage({ type: 'error', text: msg });
    } finally {
      setApplicationSubmitting(false);
    }
  };

  const getSpeciesIcon = () => {
    return <PetsIcon />;
  };

  const getGenderIcon = (gender: string | null | undefined) => {
    const g = gender === 'male' || gender === 'M' ? 'male' : 'female';
    return g === 'male' ? <MaleIcon /> : <FemaleIcon />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'reserved':
        return 'warning';
      case 'adopted':
        return 'info';
      default:
        return 'default';
    }
  };

  const resetFilters = () => {
    setFilters({
      species: '',
      ageRange: '',
      size: '',
      gender: '',
      status: 'available',
      city: '',
      county: '',
      q: '',
    });
    setPage(1);
  };

  const applyFiltersClick = () => {
    setPage(1);
    loadAnimals();
  };

  if (loading && animals.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={4}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <PetsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Animale disponibile pentru adopție
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Catalog real din adăposturi partenere — filtre și cerere online
            </Typography>
          </Box>
        </Stack>
      </Box>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Căutare
          </Typography>

          <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Caută"
                placeholder="Nume, rasă, descriere..."
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Oraș"
                value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                InputProps={{
                  startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Județ"
                value={filters.county}
                onChange={(e) => setFilters({ ...filters, county: e.target.value })}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Specie"
                value={filters.species}
                onChange={(e) => setFilters({ ...filters, species: e.target.value })}
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="dog">Câine</MenuItem>
                <MenuItem value="cat">Pisică</MenuItem>
                <MenuItem value="bird">Pasăre</MenuItem>
                <MenuItem value="rabbit">Iepure</MenuItem>
                <MenuItem value="horse">Cal</MenuItem>
                <MenuItem value="other">Altele</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Vârstă (grup)"
                value={filters.ageRange}
                onChange={(e) => setFilters({ ...filters, ageRange: e.target.value })}
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="puppy">Pui (sub 1 an)</MenuItem>
                <MenuItem value="junior">Tineri (1–2 ani)</MenuItem>
                <MenuItem value="adult">Adulți (3–6 ani)</MenuItem>
                <MenuItem value="senior">Seniori (7+ ani)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Talie"
                value={filters.size}
                onChange={(e) => setFilters({ ...filters, size: e.target.value })}
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="mica">Mică</MenuItem>
                <MenuItem value="medie">Medie</MenuItem>
                <MenuItem value="mare">Mare</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Gen"
                value={filters.gender}
                onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="male">Mascul</MenuItem>
                <MenuItem value="female">Femelă</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="available">Disponibil</MenuItem>
                <MenuItem value="reserved">Rezervat</MenuItem>
                <MenuItem value="adopted">Adoptat</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" size="small" onClick={resetFilters}>
              Resetează filtrele
            </Button>
            <Button variant="contained" size="small" startIcon={<FilterIcon />} onClick={applyFiltersClick}>
              Aplică filtrele
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {animals.length === 0 && !loading ? (
        <Alert severity="info">Nu există animale care să corespundă criteriilor.</Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {animals.map((animal) => {
              const cover = resolvePhotoUrl(animal.photos[0]);
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={animal.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardMedia
                      component="div"
                      sx={{
                        height: 200,
                        bgcolor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        backgroundImage: cover ? `url(${cover})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    >
                      {!cover ? getSpeciesIcon() : null}
                      <Chip
                        label={
                          animal.status === 'available'
                            ? 'Disponibil'
                            : animal.status === 'reserved'
                              ? 'Rezervat'
                              : 'Adoptat'
                        }
                        color={getStatusColor(animal.status)}
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                    </CardMedia>

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {animal.name}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            {getGenderIcon(animal.gender)}
                            <Typography variant="body2" color="text.secondary">
                              {animal.breed || '—'} • {animal.species}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {animal.age_months != null
                              ? `${Math.floor(animal.age_months / 12)} ani`
                              : 'Vârstă necunoscută'}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {animal.sterilized && (
                            <Chip icon={<SterilizedIcon />} label="Sterilizat" size="small" color="success" />
                          )}
                          {animal.vaccinated && (
                            <Chip icon={<VaccinatedIcon />} label="Vaccinat" size="small" color="success" />
                          )}
                          {animal.microchipped && <Chip label="Microcip" size="small" color="info" />}
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {animal.shelter.name}, {animal.shelter.city}
                          </Typography>
                        </Stack>

                        {animal.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {animal.description}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>

                    <CardActions>
                      <Stack direction="row" spacing={1} sx={{ width: '100%', flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewAnimal(animal)}
                          sx={{ flexGrow: 1 }}
                        >
                          Detalii
                        </Button>
                        {animal.status === 'available' && (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<ApplicationIcon />}
                              onClick={() => handleApplyForAdoption(animal)}
                            >
                              Cerere adopție
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ScheduleIcon />}
                              onClick={() => handleScheduleVisit(animal)}
                            >
                              Contact adăpost
                            </Button>
                          </>
                        )}
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {total > PAGE_SIZE && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.max(1, pages)}
                page={page}
                onChange={(_e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalii animal</DialogTitle>
        <DialogContent>
          {detailLoading && <CircularProgress size={32} sx={{ m: 2 }} />}
          {selectedAnimal && !detailLoading && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {selectedAnimal.name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                  {getGenderIcon(selectedAnimal.gender)}
                  <Typography variant="body1">
                    {selectedAnimal.breed || '—'} • {selectedAnimal.species}
                  </Typography>
                  <Chip
                    label={
                      selectedAnimal.status === 'available'
                        ? 'Disponibil'
                        : selectedAnimal.status === 'reserved'
                          ? 'Rezervat'
                          : 'Adoptat'
                    }
                    color={getStatusColor(selectedAnimal.status)}
                    size="small"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <CalendarIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  {selectedAnimal.age_months != null
                    ? ` ~${Math.floor(selectedAnimal.age_months / 12)} ani (${selectedAnimal.age_months} luni)`
                    : 'Vârstă necunoscută'}
                </Typography>
              </Box>

              {selectedAnimal.description && (
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Descriere
                  </Typography>
                  <Typography variant="body1">{selectedAnimal.description}</Typography>
                </Box>
              )}

              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Sănătate / identificare
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {selectedAnimal.sterilized && (
                    <Chip icon={<SterilizedIcon />} label="Sterilizat" color="success" />
                  )}
                  {selectedAnimal.vaccinated && (
                    <Chip icon={<VaccinatedIcon />} label="Vaccinat" color="success" />
                  )}
                  {selectedAnimal.dewormed && <Chip label="Deparazitat" color="success" />}
                  {selectedAnimal.microchipped && <Chip label="Microcip" color="info" />}
                </Stack>
              </Box>

              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Adăpost
                </Typography>
                <Typography variant="body1">{selectedAnimal.shelter.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAnimal.shelter.city}, {selectedAnimal.shelter.county}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Închide</Button>
          {selectedAnimal?.status === 'available' && (
            <>
              <Button
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleScheduleVisit(selectedAnimal);
                }}
              >
                Contact adăpost
              </Button>
              <Button
                variant="contained"
                startIcon={<FavoriteIcon />}
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleApplyForAdoption(selectedAnimal);
                }}
              >
                Cerere adopție
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={applicationDialogOpen}
        onClose={() => !applicationSubmitting && setApplicationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cerere de adopție</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!user && (
              <Alert severity="warning">Autentifică-te pentru a trimite cererea. Datele din profil vor fi folosite de adăpost.</Alert>
            )}
            {user && (
              <Alert severity="info">
                Cererea se trimite în numele contului <strong>{user.email}</strong>. Poți adăuga un mesaj pentru adăpost.
              </Alert>
            )}
            {applicationMessage && (
              <Alert severity={applicationMessage.type === 'success' ? 'success' : 'error'}>
                {applicationMessage.text}
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Mesaj pentru adăpost (opțional)"
              value={applicationNotes}
              onChange={(e) => setApplicationNotes(e.target.value)}
              placeholder="Experiență cu animale, program, întrebări..."
              disabled={applicationSubmitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={applicationSubmitting} onClick={() => setApplicationDialogOpen(false)}>
            Anulează
          </Button>
          <Button
            variant="contained"
            disabled={applicationSubmitting}
            onClick={() => (user ? handleSubmitApplication() : navigate('/login', { state: { from: '/adoptie' } }))}
          >
            {user ? (applicationSubmitting ? 'Se trimite…' : 'Trimite cererea') : 'Autentificare'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AnimalsListingPage;
